import React, { useEffect, useRef } from 'react';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
  const sectionRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -10% 0px' }
    );

    sectionRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      sectionRefs.current.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, []);

  return (
    <div className="privacy-policy-page">
      <div className="privacy-hero" ref={(el) => (sectionRefs.current[0] = el)}>
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">Privacy Policy</h1>
            <p className="hero-subtitle">
              We are committed to protecting your privacy and ensuring the security of your personal information.
            </p>
          </div>
          <div className="hero-visual">
            <div className="privacy-icon">
              <div className="shield-icon">
                <div className="shield-body"></div>
                <div className="checkmark">✓</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="privacy-content">
        <section className="policy-section" ref={(el) => (sectionRefs.current[1] = el)}>
          <div className="section-content">
            <p className="intro-text">
              Welcome to Mobius Services LLC ("we," "us," or "our"). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information when you use our website and services.
            </p>
            <p className="intro-text">
              By accessing or using our website, you signify your understanding and acceptance of this Privacy Policy. If you do not agree with our practices, please do not use our website or services.
            </p>
          </div>
        </section>

        <section className="policy-section" ref={(el) => (sectionRefs.current[2] = el)}>
          <h2 className="section-title">Information We Collect</h2>
          <div className="section-content">
            <p>We may collect certain personal information from you when you interact with our website or use our services. The types of personal information we may collect include:</p>
            <ul className="info-list">
              <li><strong>Contact Information:</strong> Your name, email address, postal address, and telephone number.</li>
              <li><strong>Account Information:</strong> Usernames, passwords, and other account credentials.</li>
              <li><strong>Payment Information:</strong> Credit card details or other payment method information.</li>
              <li><strong>Usage Information:</strong> Information about how you use our website and services, including IP addresses, browser types, and access times.</li>
              <li><strong>Correspondence:</strong> Any information you provide to us through emails, chat messages, or other communication channels.</li>
            </ul>
          </div>
        </section>

        <section className="policy-section" ref={(el) => (sectionRefs.current[3] = el)}>
          <h2 className="section-title">How We Use Your Information</h2>
          <div className="section-content">
            <p>We use the personal information we collect for various purposes, including:</p>
            <ul className="info-list">
              <li>Providing and improving our services.</li>
              <li>Processing and fulfilling your orders or requests.</li>
              <li>Communicating with you and responding to your inquiries.</li>
              <li>Sending you promotional and marketing communications (if you have opted in to receive them).</li>
              <li>Analyzing website usage and trends to enhance the user experience.</li>
              <li>Protecting the security and integrity of our website and services.</li>
              <li>Complying with legal obligations and enforcing our policies.</li>
            </ul>
          </div>
        </section>

        <section className="policy-section" ref={(el) => (sectionRefs.current[4] = el)}>
          <h2 className="section-title">Information Sharing and Disclosure</h2>
          <div className="section-content">
            <p>We may share your personal information in the following circumstances:</p>
            <ul className="info-list">
              <li>With our trusted third-party service providers who assist us in operating our website and services.</li>
              <li>With business partners and affiliates for the purpose of providing and improving our services.</li>
              <li>In response to a legal request or when required to comply with applicable laws and regulations.</li>
              <li>To protect the rights, property, or safety of Mobius Services LLC, our users, or others.</li>
              <li>With your consent or at your direction.</li>
            </ul>
          </div>
        </section>

        <section className="policy-section" ref={(el) => (sectionRefs.current[5] = el)}>
          <h2 className="section-title">Data Security</h2>
          <div className="section-content">
            <p>We take the security of your personal information seriously and implement reasonable measures to protect it from unauthorized access, disclosure, alteration, or destruction. However, please note that no method of transmission over the internet or electronic storage is 100% secure.</p>
          </div>
        </section>

        <section className="policy-section" ref={(el) => (sectionRefs.current[6] = el)}>
          <h2 className="section-title">Your Choices</h2>
          <div className="section-content">
            <p>You have certain choices regarding the personal information you provide to us:</p>
            <ul className="info-list">
              <li>You can review, update, or delete your personal information by contacting us.</li>
              <li>You can opt out of receiving promotional and marketing communications by following the instructions in the communication or contacting us.</li>
              <li>You can disable cookies or adjust your browser settings to refuse certain cookies. However, please note that some features of our website may not function properly without cookies.</li>
            </ul>
          </div>
        </section>

        <section className="policy-section" ref={(el) => (sectionRefs.current[7] = el)}>
          <h2 className="section-title">Third-Party Links</h2>
          <div className="section-content">
            <p>Our website may contain links to third-party websites or services. We are not responsible for the privacy practices or the content of those third-party websites. We encourage you to review the privacy policies of those websites before providing them with your personal information.</p>
          </div>
        </section>

        <section className="policy-section" ref={(el) => (sectionRefs.current[8] = el)}>
          <h2 className="section-title">Children's Privacy</h2>
          <div className="section-content">
            <p>Our website and services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have inadvertently collected personal information from a child, please contact us, and we will promptly delete it.</p>
          </div>
        </section>

        <section className="policy-section" ref={(el) => (sectionRefs.current[9] = el)}>
          <h2 className="section-title">Updates to this Privacy Policy</h2>
          <div className="section-content">
            <p>We may update this Privacy Policy from time to time to reflect changes in our practices or legal obligations. We will notify you of any material changes by posting the updated Privacy Policy.</p>
          </div>
        </section>

        <section className="policy-section data-retention" ref={(el) => (sectionRefs.current[10] = el)}>
          <h2 className="section-title">Data Use and Retention Policy</h2>
          <div className="section-content">
            <p>This Data Use and Retention Policy supplements our Privacy Policy and outlines the standard clauses regarding how we use and retain your personal information.</p>
            
            <h3 className="subsection-title">Data Use</h3>
            <p><strong>Purpose of Data Use:</strong> We collect and use your personal information for specific purposes, including:</p>
            <ul className="info-list">
              <li>Providing and improving our services.</li>
              <li>Processing and fulfilling your orders or requests.</li>
              <li>Communicating with you and responding to your inquiries.</li>
              <li>Sending you promotional and marketing communications (if you have opted in to receive them).</li>
              <li>Analyzing website usage and trends to enhance the user experience.</li>
              <li>Protecting the security and integrity of our website and services.</li>
              <li>We comply with legal obligations and enforce our policies.</li>
            </ul>
            
            <p><strong>Consent:</strong> We only use your personal information when you have provided your explicit consent or when it is necessary to fulfill a contract, legal obligation, or legitimate interest.</p>
            
            <p><strong>Data Minimization:</strong> We limit the collection and use of personal information to what is necessary for the stated purposes. We do not use your data for purposes unrelated to those described in our Privacy Policy.</p>
            
            <h3 className="subsection-title">Data Retention</h3>
            <p><strong>Retention Period:</strong> We retain your personal information only for as long as it is necessary to fulfill the purposes for which it was collected, including legal, accounting, or reporting requirements.</p>
            
            <p><strong>Your Rights:</strong> You have the right to request access to your personal information, as well as to request correction, deletion, or restriction of its use in accordance with applicable data protection laws. To exercise these rights, please contact us.</p>
            
            <p><strong>Account Deletion:</strong> If you choose to delete your account or request the deletion of your personal information, we will make reasonable efforts to fulfill your request, unless retention is required for legal, contractual, or legitimate business purposes.</p>
            
            <p><strong>Data Security:</strong> We take reasonable measures to protect your personal information from unauthorized access, disclosure, alteration, or destruction. However, please note that no method of transmission over the internet or electronic storage is 100% secure.</p>
            
            <p><strong>Backup and Archiving:</strong> We may retain certain personal information in backup or archival systems for a limited period to ensure data integrity and business continuity.</p>
            
            <h3 className="subsection-title">Updates to Data Use and Retention Policy</h3>
            <p>We may update this Data Use and Retention Policy from time to time to reflect changes in our data processing practices or legal obligations. Any material changes will be notified to you in accordance with our Privacy Policy.</p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
